import React, { useEffect, useRef, useState } from 'react';
import {
    motion,
    useMotionValueEvent,
    useScroll,
    useTransform
} from 'framer-motion';

import useAnimate, { entry } from './primitives/Motion';

import GroupBadge from '../media/images/smart-solutions-group.png';
import HeroBackdrop from '../media/images/mainBackground.jpg';
import WordReveal from './primitives/WordReveal';
import { companies, contact } from '../data/site';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────────
   The hero is exactly one screen tall and is NOT pinned.

   The old version reserved 190vh with a 100svh sticky stage, so the
   stage held still for 90vh while the timeline (mapped across the
   full 190vh) only reached p = 0.47. At the moment the pin released
   the content had moved 10% and shrunk 5%: motion below the
   perceptual threshold, bought with 810px of the reader's scrolling.
   That is the "forced, doesn't do anything" the client described.

   A pin is the only mechanism on a page that can hold the viewport
   still while the scrollbar moves, so it is the only thing that can
   produce inert scroll. With it gone, `offset: ['start start',
   'end start']` maps p 0 → 1 across exactly the hero's own departure
   and every pixel of scroll necessarily reveals new material. There
   is no dedicated runway left to be inert.

   Beat map (p is the fraction of the hero that has left the top):

     .00 → .10   scroll cue dismissed
     .03 → .40   three accent segments DRAW IN beneath the chips,
                 staggered: the first thing scrolling does is add
                 something, not subtract it
     .05 → .32   badge + headline dissolve as they cross the top
     .12 → .44   lead paragraph
     .20 → .56   call-to-action buttons
     .32 → .70   the three chips, staggered: they outlive the
                 headline and are the last copy on screen
     .44 → .72   one neutral hairline resolves over the three accent
                 segments: three companies, one standard; and the
                 next thing to enter the viewport is the trade
                 ticker's own hairline border
     .00 → 1.0   backdrop lags (+7vh) while the content leads (−9vh);
                 16vh of plane separation over the departure

   Amplitudes are viewport-relative (vh), not percentages of the
   content block, so the parallax reads the same at 1920×1080 and
   390×844: the block itself is ~2× taller on a phone.
   ───────────────────────────────────────────────────────────────── */

const T = {
    cueOut: 0.10,
    introOut: [0.05, 0.32],
    leadOut: [0.12, 0.44],
    actionsOut: [0.20, 0.56],
    chipIn: 0.32,
    chipOut: 0.70,
    unify: [0.44, 0.72],
    seam: [0.03, 0.30],
    /* Once a group is fully transparent it must stop being clickable
       and focusable, or an invisible phone-number link sits in the
       middle of the screen. */
    actionsSpent: 0.58,
    chipsSpent: 0.74,
    /* The aurora is a full-viewport `filter: blur(70px)` wrapping
       three children on infinite transform keyframes, so the browser
       re-rasterises a blurred full-screen surface every frame, for
       the whole session, not just the hero. Park it once the hero is
       behind the reader. */
    settle: 0.55
};

/* Hooks cannot run inside .map(), so each repeated element owns its own. */

function Chip({ company, index, progress, live }) {
    const opacity = useTransform(
        progress,
        [T.chipIn + index * 0.03, T.chipOut + index * 0.03],
        [1, 0]
    );

    const style = { '--accent': company.accent };

    return (
        <motion.li style={live ? { ...style, opacity } : style}>
            {/* The entry animation lives on the <a> and the scroll opacity on
                the <li>: two owners of one `opacity` silently fight, and the
                motion value in `style` always wins. */}
            <motion.a
                href={`#${company.id}`}
                {...entry(live, { opacity: 0, y: 14 })}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.25 + index * 0.09, ease: EASE }}
            >
                <span className="hero__chip-dot" />
                {company.discipline}
            </motion.a>
        </motion.li>
    );
}

function SeamBar({ company, index, progress, live }) {
    const scaleX = useTransform(
        progress,
        [T.seam[0] + index * 0.05, T.seam[1] + index * 0.05],
        [0, 1]
    );

    const style = { '--accent': company.accent };

    return (
        <motion.span
            className="hero__seam-bar"
            style={live ? { ...style, scaleX } : style}
        />
    );
}

export default function Hero() {
    const ref = useRef(null);
    const reduced = !useAnimate();
    const live = !reduced;

    /* One screen tall and unpinned, so p = 0 at rest and p = 1 the
       instant the hero's bottom edge reaches the top of the viewport.
       The timeline cannot outrun the section. */
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start']
    });

    /* NEAR: leads the page. Raw progress, so it stays under the finger. */
    const contentY = useTransform(scrollYProgress, [0, 0.8], ['0vh', '-9vh']);

    /* Each group fades as it crosses the top edge, rather than the whole
       block dimming while it sits squarely in the middle of the screen. */
    const introOpacity = useTransform(scrollYProgress, T.introOut, [1, 0]);
    const leadOpacity = useTransform(scrollYProgress, T.leadOut, [1, 0]);
    const actionsOpacity = useTransform(scrollYProgress, T.actionsOut, [1, 0]);

    /* Three accents resolve into one hairline, a cross-dissolve, so the
       colour genuinely leaves rather than tinting the line that replaces it.
       Two opacity writes; nothing repaints. */
    const barsOpacity = useTransform(scrollYProgress, T.unify, [1, 0]);
    const unifyOpacity = useTransform(scrollYProgress, T.unify, [0, 1]);

    /* FAR: lags the page, and keeps going after everything else has left,
       because its lower band is the only hero surface still on screen. */
    const backdropY = useTransform(scrollYProgress, [0, 1], ['0vh', '7vh']);
    const backdropScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.14]);

    const cueOpacity = useTransform(scrollYProgress, [0, T.cueOut], [1, 0]);

    /* Three booleans for the whole departure. Each flips once; React bails
       out of the render when nothing changed. */
    const [phase, setPhase] = useState({
        actionsSpent: false,
        chipsSpent: false,
        settled: false
    });

    const applyPhase = value => {
        const next = {
            actionsSpent: value > T.actionsSpent,
            chipsSpent: value > T.chipsSpent,
            settled: value > T.settle
        };

        setPhase(prev =>
            prev.actionsSpent === next.actionsSpent &&
            prev.chipsSpent === next.chipsSpent &&
            prev.settled === next.settled
                ? prev
                : next
        );
    };

    useMotionValueEvent(scrollYProgress, 'change', value => {
        if (live) applyPhase(value);
    });

    /* Scroll restoration and deep links can land mid-page with no change
       event ever firing. */
    useEffect(() => {
        if (live) applyPhase(scrollYProgress.get());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live]);

    const actionsGone = live && phase.actionsSpent;
    const chipsGone = live && phase.chipsSpent;

    return (
        <section className="hero" id="top" ref={ref}>
            <motion.div
                className="hero__backdrop"
                aria-hidden="true"
                style={live ? { y: backdropY, scale: backdropScale } : undefined}
            >
                <img src={HeroBackdrop} alt="" />
            </motion.div>

            <div
                className="hero__aurora"
                data-settled={phase.settled ? 'true' : 'false'}
                aria-hidden="true"
            >
                <span className="hero__blob hero__blob--tech" />
                <span className="hero__blob hero__blob--electric" />
                <span className="hero__blob hero__blob--construction" />
            </div>

            <div className="hero__vignette" aria-hidden="true" />

            <motion.div
                className="hero__content"
                style={live ? { y: contentY } : undefined}
            >
                <motion.div
                    className="hero__intro"
                    style={live ? { opacity: introOpacity } : undefined}
                >
                    {/* The float lives on a wrapper: a CSS animation on
                        `transform` outranks the inline transform framer writes,
                        so the badge's entry scale never rendered while the two
                        shared an element. */}
                    <span className="hero__badge-float">
                        <motion.img
                            className="hero__badge"
                            src={GroupBadge}
                            alt="The Smart Solutions Group"
                            width="120"
                            height="120"
                            {...entry(live, { opacity: 0, scale: 0.72, filter: 'blur(14px)' })}
                            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                            transition={{ duration: 1.4, ease: EASE }}
                        />
                    </span>

                    <WordReveal
                        className="t-display hero__title"
                        text="Three companies. One standard."
                        delay={0.32}
                    />
                </motion.div>

                <motion.div
                    className="hero__plane"
                    style={live ? { opacity: leadOpacity } : undefined}
                >
                    <motion.p
                        className="t-lead hero__lead"
                        {...entry(live, { opacity: 0, y: 22 })}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.85, ease: EASE }}
                    >
                        Low voltage. High voltage. Interior construction. Every trade your
                        project needs, under a single point of contact.
                    </motion.p>
                </motion.div>

                <motion.div
                    className="hero__plane"
                    style={live ? { opacity: actionsOpacity } : undefined}
                    inert={actionsGone ? '' : undefined}
                    aria-hidden={actionsGone ? 'true' : undefined}
                >
                    <motion.div
                        className="hero__actions"
                        {...entry(live, { opacity: 0, y: 22 })}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1, ease: EASE }}
                    >
                        <a className="btn btn--solid" href="#companies">
                            Explore the group
                            <span className="btn__arrow" aria-hidden="true">&#8594;</span>
                        </a>
                        <a className="btn btn--ghost" href={contact.phoneHref}>
                            Call {contact.phone}
                        </a>
                    </motion.div>
                </motion.div>

                <ul
                    className="hero__chips"
                    inert={chipsGone ? '' : undefined}
                    aria-hidden={chipsGone ? 'true' : undefined}
                >
                    {companies.map((company, i) => (
                        <Chip
                            key={company.id}
                            company={company}
                            index={i}
                            progress={scrollYProgress}
                            live={live}
                        />
                    ))}
                </ul>

                {/* The handoff. Three accent segments draw in on the first
                    pixels of scroll, then one neutral hairline resolves over
                    them, and the very next horizontal line the reader meets
                    is the trade ticker's own 1px border. The hero doesn't fade
                    to black, it hands off. Nothing is added to the resting
                    composition: the segments only exist as a consequence of
                    scrolling. */}
                <div className="hero__seam" aria-hidden="true">
                    <motion.div
                        className="hero__seam-bars"
                        style={live ? { opacity: barsOpacity } : undefined}
                    >
                        {companies.map((company, i) => (
                            <SeamBar
                                key={company.id}
                                company={company}
                                index={i}
                                progress={scrollYProgress}
                                live={live}
                            />
                        ))}
                    </motion.div>

                    <motion.span
                        className="hero__seam-unify"
                        style={live ? { opacity: unifyOpacity } : undefined}
                    />
                </div>
            </motion.div>

            <motion.a
                className="hero__cue"
                href="#manifesto"
                aria-label="Scroll to continue"
                style={live ? { opacity: cueOpacity } : undefined}
            >
                <span className="hero__cue-track"><span className="hero__cue-dot" /></span>
                <span className="hero__cue-label">Scroll</span>
            </motion.a>
        </section>
    );
}
